import React, { useState, useEffect, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import "bootstrap/dist/css/bootstrap.min.css";
import "../../styles/KebunLP.css";
import axios from "axios";
import Swal from "sweetalert2";
import {
  LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer
} from 'recharts';

const KebunPabrik = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [pabrikData, setPabrikData] = useState([]);
  const [dataGrafikGabungan, setDataGrafikGabungan] = useState([]);
  const [daftarPabrikNama, setDaftarPabrikNama] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingGrafik, setLoadingGrafik] = useState(true);
  const [tanggal, setTanggal] = useState("");
  const [userInfo, setUserInfo] = useState({
    role: "Guest",
    name: "Guest",
    full_name: "Guest",
  });

  const COLORS = ['#198754', '#f6c23e', '#e74a3b', '#36b9cc', '#4e73df', '#858796', '#20c997', '#fd7e14'];

  const formatNumber = (value) => {
    if (value === null || value === undefined || isNaN(Number(value))) return "-";
    return new Intl.NumberFormat("id-ID").format(value);
  };

  const fetchPabrikData = useCallback(async (showLoading = true) => {
    if (showLoading) setLoading(true);
    try {
      const response = await axios.get(
        "https://c-greenproject.org:7500/daftar_pabrik",
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${localStorage.getItem("token")}`,
          },
        }
      );

      if (response.data.pabrik_list) {
        setPabrikData(response.data.pabrik_list);
      }
    } catch (error) {
      console.error("Error fetching pabrik data:", error);
      Swal.fire("Error", "Gagal mengambil data pabrik.", "error");
    } finally {
      if (showLoading) setLoading(false);
    }
  }, []);

  const fetchGrafikGabungan = async () => {
    try {
      const response = await axios.get(
        "https://c-greenproject.org:7500/grafik/semua_pabrik/tren_cpo",
        { headers: { Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );

      if (response.data.data_gabungan) {
        setDataGrafikGabungan(response.data.data_gabungan);
        setDaftarPabrikNama(response.data.daftar_pabrik);
      }
    } catch (error) {
      console.error("Error fetching grafik gabungan:", error);
    } finally {
      setLoadingGrafik(false);
    }
  };

  useEffect(() => {
    setTanggal(
      new Date().toLocaleDateString("id-ID", {
        weekday: "long",
        day: "numeric",
        month: "long",
        year: "numeric",
      })
    );

    const fetchUserInfo = async () => {
      try {
        const response = await axios.get(
          "https://c-greenproject.org:7500/get_user_role",
          { withCredentials: true }
        );
        if (!response.data.error) {
          setUserInfo(response.data);
        }
      } catch (error) {
        console.error("Error fetching user info:", error);
      }
    };

    fetchUserInfo();
    fetchPabrikData(true);
    fetchGrafikGabungan();
  }, [fetchPabrikData]);

  const handleLogout = async () => {
    try {
      await axios.post(
        "https://c-greenproject.org:7500/logout",
        {},
        { withCredentials: true }
      );
      navigate("/");
    } catch (error) {
      Swal.fire("Gagal", "Gagal logout.", "error");
    }
  };

  const handlePilih = async (pabrikId) => {
    const { value: rencanaPanenId } = await Swal.fire({
      title: "Masukkan Rencana Panen",
      input: "text",
      inputLabel: "ID Rencana Panen",
      inputPlaceholder: "Contoh: 123",
      showCancelButton: true,
      confirmButtonText: "Simpan",
      cancelButtonText: "Batal",
      confirmButtonColor: "#198754",
      cancelButtonColor: "#6c757d",
      inputValidator: (value) => {
        if (!value) return "Rencana Panen ID tidak boleh kosong!";
      },
    });

    if (!rencanaPanenId) return;

    try {
      const response = await axios.post(
        "https://c-greenproject.org:7500/pilih_pabrik",
        { rencana_panen_id: rencanaPanenId, pabrik_id: pabrikId },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );

      if (response.data.error) {
        Swal.fire("Gagal", response.data.error, "error");
      } else {
        Swal.fire("Berhasil", response.data.message || "Pabrik berhasil dipilih", "success");
      }
    } catch (error) {
      Swal.fire("Gagal", error.response?.data?.error || "Terjadi kesalahan.", "error");
    }
  };

  const handleUpdateKapasitas = async (pabrikId) => {
    try {
      const response = await axios.post(
        "https://c-greenproject.org:7500/update_kapasitas_per_jam_kebun",
        { pabrik_id: pabrikId },
        { headers: { "Content-Type": "application/json", Authorization: `Bearer ${localStorage.getItem("token")}` } }
      );

      if (response.data.error) {
        Swal.fire("Gagal", response.data.error, "error");
      } else {
        Swal.fire({
          title: "Berhasil",
          text: response.data.message || "Kapasitas berhasil diperbarui",
          icon: "success",
          timer: 1500,
          showConfirmButton: false
        });
        fetchPabrikData(false); 
      }
    } catch (error) {
      Swal.fire("Error", "Terjadi kesalahan saat memperbarui kapasitas.", "error");
    }
  };

  return (
    <div className="d-flex flex-column bg-light min-vh-100">
      {/* Header */}
      <div className="header bg-white shadow-sm border-bottom">
        <img src="/static/logo_potrait.png" alt="Logo" />
        <div className="top-right">
          <span className="fw-medium text-secondary">{userInfo.full_name} |</span>
          <button className="btn btn-outline-danger btn-sm rounded-pill px-3 ms-2" onClick={handleLogout}>
            Keluar
          </button>
        </div>
      </div>

      <div className="d-flex flex-grow-1">
        {/* Sidebar */}
        <div className="sidebar p-3 bg-white shadow-sm" id="sidebar">
          <p id="sidebar-title" className="text-muted small mb-1">Portal Sistem Traceability</p>
          <h6 id="sidebar-subtitle" className="fw-bold text-success mb-3">Telsa Super Apps</h6>
          <p id="sidebar-date" className="bg-light p-2 rounded-3 text-muted small fw-medium d-flex align-items-center gap-2">
            <img src="/static/schedule_11145854.png" alt="Date" style={{ width: "16px" }} />
            {tanggal}
          </p>
          <div className="list-group list-group-flush mt-3" id="sidebar-menu">
            <div onClick={() => navigate("/kebun")} className="list-group-item border-0 rounded-3 mb-1" style={{ cursor: "pointer" }}>
              Beranda
            </div>
            <div onClick={() => navigate("/KebunP")} className="list-group-item border-0 rounded-3 mb-1" style={{ cursor: "pointer" }}>
              Petani
            </div>
            <div onClick={() => navigate("/KebunL")} className="list-group-item border-0 rounded-3 mb-1" style={{ cursor: "pointer" }}>
              Logistik
            </div>
            <div onClick={() => navigate("/kebunpabrik")} className="list-group-item border-0 rounded-3 mb-1 bg-success text-white" style={{ cursor: "pointer" }}>
              Pabrik
            </div>
          </div>
        </div>

        {/* Main Content */}
        <div className="main-content flex-grow-1 p-4 w-100">
          <div className="d-flex justify-content-between align-items-center mb-4">
            <h2 className="fw-bold text-dark mb-0">Rincian Pabrik & Analitik</h2>
          </div>

          <div className="divider mb-4"></div>

          {/* BAGIAN GRAFIK GABUNGAN DI ATAS TABEL */}
          <div className="bg-white p-4 rounded-4 shadow-sm mb-4">
            <h5 className="fw-bold text-success mb-2">Komparasi Tren Produksi CPO Seluruh Pabrik</h5>
            <p className="text-muted small mb-3">Grafik gabungan yang membandingkan hasil produksi CPO antar pabrik dalam satu lini waktu.</p>
            
            {loadingGrafik ? (
              <div className="text-center py-4">
                <div className="spinner-border text-success spinner-border-sm" role="status"></div>
                <span className="text-muted ms-2 small">Memuat grafik...</span>
              </div>
            ) : dataGrafikGabungan.length > 0 ? (
              <ResponsiveContainer width="100%" height={320}>
                <LineChart data={dataGrafikGabungan} margin={{ top: 10, right: 30, left: 10, bottom: 10 }}>
                  <CartesianGrid stroke="#ccc" strokeDasharray="5 5" vertical={false} />
                  <XAxis dataKey="waktu" tick={{ fontSize: 11 }} />
                  <YAxis tick={{ fontSize: 11 }} width={70} />
                  <Tooltip formatter={(value, name) => [`${formatNumber(value)} Kg`, name]} />
                  <Legend wrapperStyle={{ fontSize: "12px", paddingTop: "10px" }} />
                  {daftarPabrikNama.map((namaPabrik, index) => (
                    <Line
                      key={namaPabrik}
                      type="monotone"
                      dataKey={namaPabrik}
                      name={namaPabrik}
                      stroke={COLORS[index % COLORS.length]}
                      strokeWidth={2}
                      dot={{ r: 3 }}
                      activeDot={{ r: 5 }}
                      connectNulls
                    />
                  ))}
                </LineChart>
              </ResponsiveContainer>
            ) : (
              <div className="text-center py-3 text-muted small">
                Belum ada data historis produksi CPO gabungan yang tercatat.
              </div>
            )}
          </div>

          {/* TABLE CONTAINER CARD */}
          <div className="content-box p-4 border-0 shadow-sm rounded-4 bg-white">
            <div className="d-flex justify-content-between align-items-center mb-3">
              <h4 className="fw-bold text-success mb-0">Daftar Pabrik Tersedia</h4>
            </div>

            {loading ? (
              <div className="text-center py-5">
                <div className="spinner-border text-success" role="status"></div>
                <p className="text-muted mt-2">Memuat data pabrik...</p>
              </div>
            ) : pabrikData && pabrikData.length > 0 ? (
              <div className="table-responsive" style={{ overflowX: "auto" }}>
                <table className="table table-hover align-middle" style={{ minWidth: "1100px" }}>
                  <thead className="table-light text-nowrap">
                    <tr>
                      <th className="text-center py-3" style={{ width: "50px", borderTopLeftRadius: "10px" }}>No</th>
                      <th className="py-3" style={{ width: "250px" }}>Informasi Pabrik</th>
                      <th className="text-center py-3" style={{ width: "120px" }}>Status Operasi</th>
                      <th className="text-center py-3" style={{ width: "160px" }}>Kapasitas Maksimal</th>
                      <th className="text-center py-3" style={{ width: "200px" }}>Kapasitas Tersedia</th>
                      <th className="text-center py-3" style={{ width: "250px", borderTopRightRadius: "10px" }}>Aksi</th>
                    </tr>
                  </thead>
                  <tbody>
                    {pabrikData.map((pabrik, index) => (
                      <tr key={`pabrik-${pabrik.pabrik_id || index}`}>
                        <td className="text-center fw-bold text-muted">{index + 1}</td> 
                        <td className="py-3">
                          <div className="fw-bold text-dark text-capitalize mb-1">{pabrik.nama || "-"}</div>
                          <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                            <i className="fa fa-map-marker-alt me-1 text-secondary"></i> {pabrik.alamat_pabrik || "-"}
                          </div>
                          <div className="text-muted" style={{ fontSize: "0.8rem" }}>
                            <i className="fa fa-phone-alt me-1 text-secondary"></i> {pabrik.nomor_hp || "-"}
                          </div>
                        </td>
                        <td className="text-center">
                          {pabrik.status_penerimaan?.toLowerCase() === "buka" ? (
                            <span className="badge bg-success-subtle text-success border border-success-subtle px-3 py-1 rounded-pill">Buka</span>
                          ) : (
                            <span className="badge bg-danger-subtle text-danger border border-danger-subtle px-3 py-1 rounded-pill">Tutup</span>
                          )}
                          <div className="mt-2 text-muted fw-semibold" style={{ fontSize: "0.8rem" }}>
                            <i className="fa fa-truck me-1"></i> {pabrik.truk_menuju_pabrik || 0} Truk OTW
                          </div>
                        </td>
                        <td className="text-center font-monospace text-secondary">
                          {formatNumber(pabrik.kapasitas_maksimal)} <span className="small text-muted">Kg</span>
                        </td>
                        <td className="text-center">
                          <div className="d-flex align-items-center justify-content-center gap-2 mb-1">
                            <span className="fw-bold text-dark font-monospace" style={{ fontSize: "1.05rem" }}>
                              {formatNumber(pabrik.kapasitas_tersedia)}
                            </span>
                            <button
                              className="btn btn-outline-warning btn-sm rounded-pill px-3 fw-medium text-nowrap shadow-sm"
                              onClick={() => handleUpdateKapasitas(pabrik.pabrik_id)}
                              style={{ padding: "0.2rem 0.6rem", fontSize: "0.8rem" }}
                            >
                              Update
                            </button>
                          </div>
                          <div className="text-muted fst-italic" style={{ fontSize: "0.75rem" }}>
                            Update: {pabrik.terakhir_diperbarui || "Belum ada"}
                          </div>
                        </td>
                        <td className="text-center">
                          <div className="d-flex justify-content-center gap-2">
                            <button
                              className="btn btn-outline-info btn-sm rounded-pill px-3 fw-bold shadow-sm"
                              onClick={() => navigate(`/kebunpabrik/detail/${pabrik.pabrik_id}`, { state: { pabrikInfo: pabrik } })}
                            >
                              <i className="fa fa-info-circle me-1"></i> Detail
                            </button>
                            <button
                              className="btn btn-outline-primary btn-sm rounded-pill px-4 fw-bold shadow-sm"
                              onClick={() => handlePilih(pabrik.pabrik_id)}
                            >
                              Pilih
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <div className="text-center py-5 text-muted bg-light rounded-3 mt-3">
                <i className="fa fa-industry mb-3 opacity-50" style={{ fontSize: "3rem" }}></i>
                <h5 className="fw-semibold">Tidak Ada Data Pabrik</h5>
                <p className="mb-0">Belum ada informasi pabrik yang tersedia saat ini.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default KebunPabrik;